import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Loader2, UploadCloud, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

import { useMediaUpload } from '../lib';

import { useMediaFolder } from './media-folder';
import { resolveMediaBatch } from './media.api';

interface FixedMediaSlotsFieldProps {
  /** Ordered list of media ids (only the FILLED slots, compacted to the front). */
  value: string[];
  onChange: (ids: string[]) => void;
  /** How many fixed slots to always render (e.g. 3 horizontal images). */
  count: number;
  label?: string;
  folder?: string;
  disabled?: boolean;
  /** Aspect ratio class for each slot tile. Defaults to landscape (3:2). */
  aspectClassName?: string;
  /** Columns for the slot grid. Defaults to the slot count. */
  gridClassName?: string;
}

/** One filled, reorderable slot. Drag anywhere on the tile; the X clears it. */
const SortableSlot = ({
  id,
  url,
  onRemove,
  disabled,
  aspectClassName,
}: {
  id: string;
  url: string | undefined;
  onRemove: () => void;
  disabled?: boolean;
  aspectClassName: string;
}): React.JSX.Element => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
      }}
      className={`group relative ${aspectClassName} overflow-hidden rounded-lg border border-(--border) bg-(--z-50)`}
    >
      {url ? (
        <img
          src={url}
          alt=""
          className={`h-full w-full object-cover ${disabled ? '' : 'cursor-grab active:cursor-grabbing'}`}
          {...attributes}
          {...listeners}
        />
      ) : (
        <div className="flex h-full items-center justify-center">
          <Loader2 className="h-4 w-4 animate-spin text-(--fg-faint)" />
        </div>
      )}
      {!disabled && (
        <button
          type="button"
          onClick={onRemove}
          className="absolute top-1 right-1 rounded bg-black/60 p-0.5 text-white opacity-0 transition-opacity group-hover:opacity-100"
          aria-label="Remove image"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
};

/**
 * A gallery of a FIXED number of slots. Unlike `MediaGalleryField` (one
 * dropzone that appends), this always shows `count` blocks: filled slots
 * render a draggable preview, empty slots render an upload zone. Filled
 * images (compacted to the front of `value`) can be dragged to reorder.
 * Uploading fills the left-most empty slot; removing shifts the rest left.
 */
export const FixedMediaSlotsField = ({
  value,
  onChange,
  count,
  label,
  folder,
  disabled,
  aspectClassName = 'aspect-[3/2]',
  gridClassName,
}: FixedMediaSlotsFieldProps): React.JSX.Element => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOverEmpty, setDragOverEmpty] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const [urls, setUrls] = useState<Record<string, string>>({});
  const contextFolder = useMediaFolder();
  const uploadFolder = folder ?? contextFolder;
  const { upload } = useMediaUpload<{ id: string }>();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // Resolve every id -> preview URL whenever the ordered list changes.
  const key = value.join(',');
  useEffect(() => {
    let cancelled = false;
    if (value.length === 0) {
      setUrls({});
      return;
    }
    resolveMediaBatch(value)
      .then((list) => {
        if (!cancelled) setUrls(Object.fromEntries(list.map((m) => [m.id, m.url])));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // `key` is the order-sensitive fingerprint of `value`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const handleFiles = async (files: FileList | File[]): Promise<void> => {
    if (disabled) return;
    const slotsLeft = Math.max(0, count - value.length);
    if (slotsLeft === 0) return;
    const images = Array.from(files)
      .filter((f) => f.type.startsWith('image/'))
      .slice(0, slotsLeft);
    if (images.length === 0) return;
    setRemaining(images.length);
    const added: string[] = [];
    for (const file of images) {
      try {
        const asset = await upload(file, { kind: 'IMAGE', folder: uploadFolder });
        if (asset?.id) added.push(asset.id);
      } catch {
        /* skip failed file, keep going */
      }
      setRemaining((r) => Math.max(0, r - 1));
    }
    if (added.length) onChange([...value, ...added].slice(0, count));
  };

  const onDragEnd = (e: DragEndEvent): void => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = value.indexOf(String(active.id));
    const to = value.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    onChange(arrayMove(value, from, to));
  };

  const uploading = remaining > 0;
  const emptyCount = Math.max(0, count - value.length);
  // The left-most empty slot is where an upload lands; it owns the spinner.
  const firstEmptyIsUploading = (slotIdx: number): boolean => uploading && slotIdx === 0;

  return (
    <div>
      {label && (
        <label className="mb-1.5 block text-xs font-medium text-(--fg-muted)">{label}</label>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={value} strategy={horizontalListSortingStrategy}>
          <div className={gridClassName ?? `grid grid-cols-${count} gap-2`}>
            {value.map((id) => (
              <SortableSlot
                key={id}
                id={id}
                url={urls[id]}
                disabled={disabled}
                aspectClassName={aspectClassName}
                onRemove={() => onChange(value.filter((x) => x !== id))}
              />
            ))}
            {Array.from({ length: emptyCount }).map((_, i) => (
              <button
                // Empty slots are positional placeholders, not data - index key is fine.
                key={`empty-${i}`}
                type="button"
                disabled={disabled || uploading}
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOverEmpty(true);
                }}
                onDragLeave={() => setDragOverEmpty(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOverEmpty(false);
                  if (e.dataTransfer.files?.length) void handleFiles(e.dataTransfer.files);
                }}
                className={`flex ${aspectClassName} flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed p-2 text-center transition-colors ${
                  dragOverEmpty
                    ? 'border-(--primary) bg-(--accent)'
                    : 'border-(--border-strong) bg-(--z-50) hover:border-(--primary)'
                } ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
              >
                {firstEmptyIsUploading(i) ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin text-(--primary)" />
                    <span className="text-[11px] text-(--fg-muted)">Uploading…</span>
                  </>
                ) : (
                  <>
                    <UploadCloud className="h-5 w-5 text-(--fg-faint)" />
                    <span className="text-[11px] text-(--fg-muted)">Add image</span>
                  </>
                )}
              </button>
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) void handleFiles(e.target.files);
          e.target.value = '';
        }}
      />
    </div>
  );
};
